import React, { useEffect, useState } from 'react';
import PollCheckbox from '../PollCheckbox';
import PollRadio from '../PollRadio';
import PollTextarea from '../PollTextarea';
import PollStarRate from '../PollStarRate';
import { Menu } from 'components/Menu';
import { SecondaryMenuItem } from 'components/Menu/SecondaryMenuItem';
import { ChevronDownIcon } from 'components/icons/ChevronDownIcon';
import api from 'services/api';
import { CircleTickIcon } from 'components/icons/CircleTickIcon';

interface ISlidoPollForm {
  pollsData: any[];
  currentPoll: any;
  setCurrentPoll: any;
  videoId: any;
  viewer: any;
  wrapperRef: any;
}

const SlidoPollForm = ({ pollsData, currentPoll, setCurrentPoll, videoId, viewer, wrapperRef }: ISlidoPollForm) => {
  const [responses, setResponses] = useState({} as any);
  const [isSubmitted, setIsSubmitted] = useState({} as any);
  const [formErrors, setFormErrors] = useState({} as any);
  const [pollsSelectedData, setPollsSelectedData] = useState({} as any);

  useEffect(() => {
    if (currentPoll && pollsSelectedData[currentPoll.hash_id]) {
      setResponses(pollsSelectedData[currentPoll.hash_id]);
    } else {
      setResponses({});
    }
    setFormErrors({});
  }, [currentPoll]);

  useEffect(() => {
    if (currentPoll?.hash_id) {
      setPollsSelectedData((prevResponses: any) => ({
        ...prevResponses,
        [currentPoll.hash_id]: responses
      }));
    }
  }, [currentPoll, responses]);

  const handleMultipleChoiceChange = (questionId: number, optionIndex: number) => {
    setResponses((prevResponses: any) => ({
      ...prevResponses,
      [questionId]: {
        ...prevResponses[questionId],
        [optionIndex]: !prevResponses[questionId]?.[optionIndex]
      }
    }));
  };

  const handleSingleChoiceChange = (questionId: number, option: any) => {
    setResponses((prevResponses: any) => ({
      ...prevResponses,
      [questionId]: option
    }));
  };

  const handleRateChange = (questionId: number, rating: number) => {
    setResponses((prevResponses: any) => ({
      ...prevResponses,
      [questionId]: rating
    }));
  };

  const handleTextResponseChange = (questionId: number, response: string) => {
    setResponses((prevResponses: any) => ({
      ...prevResponses,
      [questionId]: response
    }));
  };

  const onSubmit = () => {
    const errors = {} as any;
    const questionsAndAnswers = currentPoll.questions.map((item: any, idx: number) => {
      const { name, type } = item;
      let response = null as any;
      if (type === 'multiple') {
        response = [];
        item.answers.forEach((answerItem: any, index: number) => {
          if (responses[idx]?.[index]) {
            response.push(answerItem);
          }
        });
      } else {
        response = responses[idx];
      }
      if (item.answer_required && (!response || response.length === 0)) {
        errors[idx] = 'Required question';
      }
      return { name, type, response };
    });
    if (Object.keys(errors).length === 0) {
      api
        .post(`api/videos/${videoId}/external-polls/`, {
          viewer,
          poll: {
            title: currentPoll.title,
            poll_id: currentPoll.hash_id,
            questions: questionsAndAnswers
          }
        })
        .then(() => {
          setIsSubmitted((prev: any) => ({
            ...prev,
            [currentPoll.hash_id]: true
          }));
        });
    } else {
      const firstId = 'question' + currentPoll?.hash_id + Object.keys(errors)[0];
      const firstElement = document.getElementById(firstId);
      wrapperRef?.current?.scrollTo({ top: firstElement?.offsetTop });
      setFormErrors(errors);
    }
  };

  const renderQuestion = (question: any, questionNum: number) => {
    if (question.type === 'multiple') {
      return (
        <div
          key={'question' + currentPoll?.hash_id + questionNum}
          className={`p-2 ${formErrors[questionNum] ? 'bg-error' : ''}`}
          id={'question' + currentPoll?.hash_id + questionNum}
        >
          <p className="my-2 font-bold">
            {question.name} &nbsp;{question.answer_required ? '*' : ''} &nbsp;&nbsp;&nbsp;&nbsp;{questionNum + 1}/
            {(currentPoll?.questions || []).length}
          </p>
          <div className="flex flex-col gap-2">
            {question.answers.map((option: any, index: number) => (
              <PollCheckbox
                option={option}
                handleMultipleChoiceChange={handleMultipleChoiceChange}
                key={index}
                questionNum={questionNum}
                responses={responses}
                answerNum={index}
              />
            ))}
          </div>
          {formErrors[questionNum] ? <p className="text-orange-800 mt-2">* {formErrors[questionNum]}</p> : ''}
        </div>
      );
    } else if (question.type === 'single') {
      return (
        <div
          key={'question' + currentPoll?.hash_id + questionNum}
          className={`p-2 ${formErrors[questionNum] ? 'bg-error' : ''}`}
          id={'question' + currentPoll?.hash_id + questionNum}
        >
          <p className="my-2 font-bold">
            {question.name} &nbsp;{question.answer_required ? '*' : ''} &nbsp;&nbsp;&nbsp;&nbsp;{questionNum + 1}/
            {(currentPoll?.questions || []).length}
          </p>
          <div className="flex flex-col gap-2">
            {question.answers.map((option: any, index: number) => (
              <PollRadio
                name={question.name}
                option={option}
                handleSingleChoiceChange={handleSingleChoiceChange}
                key={index}
                questionNum={questionNum}
                responses={responses}
              />
            ))}
          </div>
          {formErrors[questionNum] ? <p className="text-orange-800 mt-2">* {formErrors[questionNum]}</p> : ''}
        </div>
      );
    } else if (question.type === 'short_answer') {
      return (
        <div
          key={'question' + currentPoll?.hash_id + questionNum}
          className={`p-2 ${formErrors[questionNum] ? 'bg-error' : ''}`}
          id={'question' + currentPoll?.hash_id + questionNum}
        >
          <p className="my-2 font-bold">
            {question.name} &nbsp;{question.answer_required ? '*' : ''} &nbsp;&nbsp;&nbsp;&nbsp;{questionNum + 1}/
            {(currentPoll?.questions || []).length}
          </p>
          <PollTextarea
            handleTextResponseChange={handleTextResponseChange}
            questionNum={questionNum}
            responses={responses}
          />
          {formErrors[questionNum] ? <p className="text-orange-800 mt-2">* {formErrors[questionNum]}</p> : ''}
        </div>
      );
    } else if (question.type === 'rating_scale') {
      return (
        <div
          key={'question' + currentPoll?.hash_id + questionNum}
          className={`p-2 ${formErrors[questionNum] ? 'bg-error' : ''}`}
          id={'question' + currentPoll?.hash_id + questionNum}
        >
          <p className="my-2 font-bold">
            {question.name} &nbsp;{question.answer_required ? '*' : ''} &nbsp;&nbsp;&nbsp;&nbsp;{questionNum + 1}/
            {(currentPoll?.questions || []).length}
          </p>
          <PollStarRate handleRateChange={handleRateChange} questionNum={questionNum} responses={responses} />
          {formErrors[questionNum] ? <p className="text-orange-800 mt-2">* {formErrors[questionNum]}</p> : ''}
        </div>
      );
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white rounded shadow">
      <p className="font-bold text-base mb-2 p-2 border-b">Polls</p>
      <div className="p-2">
        {pollsData.length > 1 && (
          <div className="bg-white w-full rounded-lg mb-2">
            <Menu
              menus={pollsData.map((poll: any) => ({
                id: poll.hash_id,
                name: poll.title,
                selected: currentPoll?.title === poll.title,
                onClick: () => {
                  setCurrentPoll(poll);
                }
              }))}
              align="center"
              position="absolute"
              top="mt-8"
              width={265}
              MenuItem={SecondaryMenuItem}
              selector={currentPoll?.hash_id}
              additionalClasses="flex justify-center"
            >
              <div className="pl-4 pr-2 h-8 leading-8 rounded-lg text-[13px] font-lato font-bold cursor-pointer w-full flex relative items-center mx-auto z-10 bg-black bg-opacity-5 hover:bg-opacity-10">
                <span className="truncate w-[calc(100%-24px)]">{currentPoll?.title}</span>
                <ChevronDownIcon />
              </div>
            </Menu>
          </div>
        )}
        {currentPoll && isSubmitted[currentPoll.hash_id] ? (
          <>
            <div className="mt-2 border rounded-md p-4 flex flex-col items-center justify-center">
              <CircleTickIcon stroke={'red'} className="w-10 h-10" />
              <p className="font-bold mt-2">Thank you for your response!</p>
            </div>
            {/* <div className="w-full flex">
              <span
                className="font-semibold mt-8 mx-auto cursor-pointer"
                onClick={() =>
                  setIsSubmitted((prev: any) => ({
                    ...prev,
                    [currentPoll.hash_id]: false
                  }))
                }
              >
                Edit Response
              </span>
            </div> */}
          </>
        ) : (
          <div>
            {(currentPoll?.questions || []).length ? (
              <>
                {(currentPoll?.questions || []).map((question: any, idx: number) => renderQuestion(question, idx))}
                <button
                  className="bg-red-main hover:bg-red-main text-white font-bold py-3 px-12 rounded focus:outline-none focus:shadow-outline my-2 ml-2"
                  onClick={onSubmit}
                >
                  Submit
                </button>
              </>
            ) : (
              <iframe
                src={currentPoll?.external_poll}
                width="100%"
                style={{ minHeight: 560, height: 'auto', marginTop: 8 }}
              ></iframe>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlidoPollForm;
